import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { computeDistanceBetween } from 'spherical-geometry-js';
import { GoogleMapComponent } from '~/_shared/components/google-map/google-map.component';
import type { GoogleMapInstance } from '~/_shared/components/google-map/useGetGoogleMapInstance.hook';
import { useOnUnmount } from '~/_shared/hooks/useOnUnmount';
import type { LatLng } from '~/_shared/types/latLng';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import { getGooglePolygonPathsForPolygon } from '~/_shared/types/polygon/polygon.utils';
import { isArray } from '~/_shared/utils/collections/collections';
import { isReportTypePolygon } from '~/exportReport/exportReport.helpers';
import { ExportReportType } from '~/exportReport/exportReportType';
import { renderExportReportMapCircles } from '~/exportReport/map/exportReportMap.helpers';
import pinIcon from '../../../../assets/images/markers/report-marker.svg';

export type ExportReportMapProps = Readonly<{
  id: string;
  className?: string;
  onMapLoaded?: () => void;
  center?: LatLng;
  radiusInMeters?: number | ReadonlyArray<number>;
  paths?: MultiPolygon;
  type: ExportReportType;
  mapType: 'roadmap' | 'hybrid';
}>;

export const ExportReportMapComponent = ({ id, className, onMapLoaded, mapType, type, center, paths, radiusInMeters }: ExportReportMapProps) => {
  const [map, setMap] = useState<GoogleMapInstance>();
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  const handleMapInit = useCallback((map: GoogleMapInstance) => {
    setMap(map);
    onMapLoaded?.();
  }, [onMapLoaded]);

  // RADIUS
  useEffect(() => {
    if (!map || type !== ExportReportType.Radius || !center || !radiusInMeters) {
      return;
    }

    const radiusList = isArray(radiusInMeters) ? radiusInMeters : [radiusInMeters];
    const highestRadius = Math.max(...radiusList);
    const circleList = renderExportReportMapCircles({ radiusList, mapInstance: map.instance, center, mapType });

    // Radius dot/center point
    const dotRadius = Math.round(Math.max(highestRadius / 20, 10));
    const dotCircle = new google.maps.Circle({
      map: map.instance,
      center,
      radius: dotRadius,
      fillColor: '#0078CE',
      fillOpacity: 0.5,
      strokeColor: '#0078CE',
      strokeOpacity: .85,
      strokeWeight: 2,
      clickable: false,
    });

    return () => {
      for (const circle of circleList) {
        circle.setMap(null);
      }

      dotCircle.setMap(null);
    };
  }, [center, map, mapType, radiusInMeters, type]);

  // LOCATION
  useEffect(() => {
    if (!map || type !== ExportReportType.Location || !center) {
      return;
    }

    map.instance.setOptions({
      center,
      mapTypeId: mapType,
      zoom: 15,
      disableDefaultUI: true,
      gestureHandling: 'none',
      clickableIcons: false,
    });

    const img = document.createElement('img');
    img.src = pinIcon;
    img.style.width = '68px';
    img.style.height = '68px';
    img.style.transform = 'translate(-50%, -50%)';
    img.style.position = 'absolute';
    img.style.margin = '-18px 0 0 16px'; // offset of the pin icon's center point

    google.maps.importLibrary('marker')
      .then((markerLibrary: google.maps.MarkerLibrary) => {
        if (!markerRef.current) {
          markerRef.current = new markerLibrary.AdvancedMarkerElement({
            map: map.instance,
            position: center,
            content: img,
          });
        }
        else {
          markerRef.current.position = center;
          markerRef.current.content = img;
        }
      })
      .catch(() => {
        console.error('Failed to load marker library');
      });

    if (!radiusInMeters) {
      return;
    }

    const radiusList = isArray(radiusInMeters) ? radiusInMeters : [radiusInMeters];
    const circleList = renderExportReportMapCircles({ radiusList, mapInstance: map.instance, center, mapType });

    return () => {
      for (const circle of circleList) {
        if (circle) {
          circle.setMap(null);
        }
      }
    };
  }, [center, map, mapType, radiusInMeters, type]);

  // POLYGON
  useEffect(() => {
    if (!map || !isReportTypePolygon(type) || !paths?.length) {
      return;
    }

    map.instance.setOptions({
      mapTypeId: mapType,
      zoom: 10,
      disableDefaultUI: true,
      gestureHandling: 'none',
      clickableIcons: false,
    });

    const polygons = paths.map(polygon => new google.maps.Polygon({
      paths: getGooglePolygonPathsForPolygon(polygon),
      map: map.instance,
      fillColor: '#0078CE',
      fillOpacity: 0.5,
      strokeColor: '#0078CE',
      strokeOpacity: 1,
      strokeWeight: 2,
      clickable: false,
    }));
    const polygonBounds = new google.maps.LatLngBounds();
    let hasBounds = false;

    paths.forEach((polygon) => {
      polygon.path.forEach((latLng) => {
        polygonBounds.extend(latLng);
        hasBounds = true;
      });
    });

    if (hasBounds) {
      map.instance.fitBounds(polygonBounds);
    }

    // DTP dot/center point
    let dotCircle: null | google.maps.Circle = null;
    if (type === ExportReportType.DriveTimePolygon) {
      const sw = polygonBounds.getSouthWest();
      const ne = polygonBounds.getNorthEast();

      const diagonal = computeDistanceBetween(
        { lat: sw.lat(), lng: sw.lng() },
        { lat: ne.lat(), lng: ne.lng() },
      );

      const dotRadius = Math.round(Math.max(diagonal * 0.015, 10));

      dotCircle = new google.maps.Circle({
        map: map.instance,
        center,
        radius: dotRadius,
        fillColor: '#0078CE',
        fillOpacity: 0.5,
        strokeColor: '#000000',
        strokeOpacity: .85,
        strokeWeight: 2,
        clickable: false,
      });
    }

    return (() => {
      polygons.forEach((polygon) => {
        polygon.setMap(null);
      });

      dotCircle?.setMap(null);
    });
  }, [center, map, mapType, paths, type]);

  useOnUnmount(() => {
    if (markerRef.current) {
      // map.instance is cached at module level and outlives this component, so the marker
      // must be explicitly deregistered from Google Maps' internal rendering layer.
      // Without this, the marker keeps rendering on the shared map instance after unmount.
      markerRef.current.map = null;
      markerRef.current = null;
    }
  });

  return (
    <GoogleMapComponent
      className={className}
      onMapInitialized={handleMapInit}
      id={id}
    />
  );
};
